import type { CrudAdapter } from '@botto/core';
import { useCrudDataSource } from '@botto/core';
import { cdpClient } from '../client';
import { activeProjectId } from '../projectScope';
import type {
  PostbackGoalConfig,
  PostbackGoalConfigCreate,
  PostbackGoalConfigUpdate,
} from '../types';

function requireProjectId(): string {
  const id = activeProjectId.value;
  if (!id) throw new Error('Выберите проект');
  return id;
}

export const postbackGoalsAdapter: CrudAdapter<
  PostbackGoalConfig,
  PostbackGoalConfigCreate,
  PostbackGoalConfigUpdate & { goal: string }
> = {
  async list({ offset, limit }) {
    const projectId = requireProjectId();
    const { data, error } = await cdpClient.GET('/projects/{project_id}/postback-goals', {
      params: { path: { project_id: projectId } },
    });
    if (error) throw error;
    const all = data?.items ?? [];
    return { items: all.slice(offset, offset + limit), total: data?.total ?? all.length };
  },
  async create(payload) {
    const projectId = requireProjectId();
    const { data, error } = await cdpClient.POST('/projects/{project_id}/postback-goals', {
      params: { path: { project_id: projectId } },
      body: payload,
    });
    if (error) throw error;
    return { id: data?.id ?? '' };
  },
  async update(payload) {
    const projectId = requireProjectId();
    const row = payload as PostbackGoalConfigUpdate & { id: string; goal?: string };
    const goal =
      row.goal ?? (await this.byKey?.(row.id))?.goal;
    if (!goal) throw new Error('goal не найден');
    const { message_id, phone_attr } = row;
    const { data, error } = await cdpClient.PATCH(
      '/projects/{project_id}/postback-goals/{goal}',
      {
        params: { path: { project_id: projectId, goal } },
        body: { message_id, phone_attr },
      },
    );
    if (error) throw error;
    return (data ?? payload) as PostbackGoalConfig;
  },
  async remove(id) {
    const projectId = requireProjectId();
    const row = await this.byKey?.(id);
    const goal = (row as PostbackGoalConfig)?.goal ?? String(id);
    const { error } = await cdpClient.DELETE('/projects/{project_id}/postback-goals/{goal}', {
      params: { path: { project_id: projectId, goal } },
    });
    if (error) throw error;
  },
  async byKey(id) {
    const projectId = requireProjectId();
    const items = await this.list({ offset: 0, limit: 10_000 });
    return items.items.find((i) => i.id === id) as PostbackGoalConfig;
  },
};

export const postbackGoalsStore = useCrudDataSource(postbackGoalsAdapter, { keyExpr: 'id' });
